import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { parseISO } from "date-fns";
import { usePageTitle } from "@/hooks/use-page-title";
import { useProjects } from "@/hooks/use-projects";
import { useTimeEntries } from "@/hooks/use-time-entries";
import { useTimeStore } from "@/stores/time-store";
import { useMe } from "@/hooks/use-me";
import { useMembers } from "@/hooks/use-members";
import { authClient } from "@/lib/auth-client";
import { createEntry, updateEntry, deleteEntry } from "@/lib/time-api";
import { groupEntriesByDay, getWeekRange, formatWeekLabel, formatDuration, toLocalDateString } from "@/lib/time-utils";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { DayTimeline } from "@/components/time/DayTimeline";
import { TimeEntryDayGroup } from "@/components/time/TimeEntryDayGroup";
import { TimeEntryDialog } from "@/components/time/TimeEntryDialog";
import { OrgTimesheetGrid } from "@/components/time/OrgTimesheetGrid";
import { ChevronLeft, ChevronRight, Plus } from "lucide-react";

export function TimePage() {
  usePageTitle("Time");
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();

  // Tab state from URL
  const VALID_TABS = ["my-time", "everyone"];
  const rawTab = searchParams.get("tab") ?? "my-time";
  const activeTab = VALID_TABS.includes(rawTab) ? rawTab : "my-time";

  if (rawTab !== activeTab) {
    setSearchParams({ tab: activeTab }, { replace: true });
  }

  function handleTabChange(tab: string) {
    setSearchParams({ tab }, { replace: true });
  }

  // Auth
  const { me } = useMe();
  const { members: orgMembers } = useMembers({ perPage: 200 });
  const { data: session } = authClient.useSession();

  // Store
  const weekOffset = useTimeStore((s) => s.weekOffset);
  const setWeekOffset = useTimeStore((s) => s.setWeekOffset);
  const entryDialogOpen = useTimeStore((s) => s.entryDialogOpen);
  const editEntry = useTimeStore((s) => s.editEntry);
  const openCreateDialog = useTimeStore((s) => s.openCreateDialog);
  const openEditDialog = useTimeStore((s) => s.openEditDialog);
  const closeEntryDialog = useTimeStore((s) => s.closeEntryDialog);

  const weekRange = getWeekRange(weekOffset);
  const weekLabel = formatWeekLabel(weekRange.start, weekRange.end);

  // Data
  const { projects } = useProjects();
  const { entries, loading } = useTimeEntries({
    startDate: weekRange.start,
    endDate: weekRange.end,
    memberId: me?.id,
    perPage: 200,
    enabled: !!me,
  });

  const dayGroups = useMemo(() => groupEntriesByDay(entries), [entries]);
  const weekTotal = entries.reduce((sum, e) => sum + e.durationSeconds, 0);

  const today = toLocalDateString(new Date());
  const todayEntries = entries.filter(
    (e) => toLocalDateString(parseISO(e.start)) === today
  );

  const projectColors = useMemo(() => {
    const map = new Map<string, string>();
    for (const p of projects) {
      if (p.color) map.set(p.id, p.color);
    }
    return map;
  }, [projects]);

  // Handlers
  async function handleEntrySubmit(data: {
    description?: string | null;
    projectId?: string | null;
    taskId?: string | null;
    start: string;
    end: string;
    createdWith?: string;
  }) {
    try {
      if (editEntry) {
        await updateEntry(editEntry.id, data);
        toast.success("Entry updated");
      } else {
        await createEntry({ ...data, createdWith: data.createdWith ?? "web" });
        toast.success("Entry created");
      }
      await queryClient.invalidateQueries({ queryKey: ["time-entries"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save entry");
    }
  }

  async function handleDelete(id: string) {
    try {
      await deleteEntry(id);
      toast.success("Entry deleted");
      await queryClient.invalidateQueries({ queryKey: ["time-entries"] });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to delete entry");
    }
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Time</h1>
        <p className="text-muted-foreground">Track and manage time entries</p>
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="my-time">My Time</TabsTrigger>
          <TabsTrigger value="everyone">Everyone</TabsTrigger>
        </TabsList>

        {/* My Time Tab */}
        <TabsContent value="my-time" className="mt-4 space-y-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setWeekOffset((o) => o - 1)}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm font-medium min-w-[180px] text-center">
                {weekLabel}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setWeekOffset((o) => o + 1)}
                disabled={weekOffset >= 0}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              {weekOffset !== 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setWeekOffset(0)}
                >
                  Today
                </Button>
              )}
            </div>
            <div className="flex items-center gap-3">
              <span className="text-sm text-muted-foreground">
                Total: {formatDuration(weekTotal)}
              </span>
              <Button size="sm" onClick={openCreateDialog}>
                <Plus className="mr-1 h-4 w-4" />
                Add Entry
              </Button>
            </div>
          </div>

          {weekOffset === 0 && todayEntries.length > 0 && (
            <DayTimeline
              entries={todayEntries}
              date={today}
              projectColors={projectColors}
            />
          )}

          {loading ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              Loading entries...
            </p>
          ) : dayGroups.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              No entries this week
            </p>
          ) : (
            <div className="space-y-6">
              {dayGroups.map((group) => (
                <TimeEntryDayGroup
                  key={group.date}
                  group={group}
                  onEdit={openEditDialog}
                  onDelete={handleDelete}
                />
              ))}
            </div>
          )}
        </TabsContent>

        {/* Everyone Tab */}
        <TabsContent value="everyone" className="mt-4">
          <OrgTimesheetGrid
            projects={projects}
            projectColors={projectColors}
            members={orgMembers}
            currentUserId={session?.user?.id}
          />
        </TabsContent>

      </Tabs>

      {/* Add/Edit entry dialog */}
      <TimeEntryDialog
        open={entryDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeEntryDialog();
        }}
        entry={editEntry}
        defaultDate={toLocalDateString(parseISO(weekRange.start))}
        projects={projects}
        existingEntries={entries}
        onSubmit={handleEntrySubmit}
      />
    </div>
  );
}
